import React, { useEffect, useState } from 'react';
import {
  CreateBase,
  SimpleForm,
  SaveButton,
  TextInput,
  Toolbar,
  useNotify,
  useRedirect,
  useCreate,
  required,
  useDataProvider,
} from "react-admin";
import { useFormContext } from 'react-hook-form';
import { fetchCurrentRoleIdFromServer } from '../../../services/auth.ts';
import { filterActionsByScope, filterMenusByScope, getManagedMenuIdSet, isQaManagerRoleName } from '../../../services/roleMenuScope.ts';
import {
  Box,
  Button,
  Chip,
  Checkbox,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

const FormSyncComponent: React.FC<{ selectedActionIds: number[] }> = ({ selectedActionIds }) => {
  const form = useFormContext();

  useEffect(() => {
    if (form) {
      form.setValue('_actionIdsSync', selectedActionIds.join(','), {
        shouldDirty: true,
      });
    }
  }, [selectedActionIds, form]);

  return null;
};

const RoleFormToolbar = () => (
  <Toolbar
    sx={{
      mt: '10px',
      position: 'sticky',
      bottom: 0,
      zIndex: 2,
      px: 0,
      pt: 2,
      pb: 1,
      bgcolor: '#ffffff',
      borderTop: '1px solid #e2e8f0',
      justifyContent: 'flex-end',
      '& .RaSaveButton-button': {
        minWidth: 120,
        height: 42,
        px: 2.5,
        borderRadius: '12px',
        textTransform: 'none',
        fontSize: '0.95rem',
        fontWeight: 700,
        background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)',
        boxShadow: '0 10px 20px rgba(79, 70, 229, 0.25)',
        '&:hover': {
          background: 'linear-gradient(135deg, #1d4ed8 0%, #4338ca 100%)',
          boxShadow: '0 12px 24px rgba(79, 70, 229, 0.32)',
        },
      },
    }}
  >
    <SaveButton sx={{  }} label="Save Role" />
  </Toolbar>
);

const RoleCreateForm: React.FC = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const [create] = useCreate();

  const [menus, setMenus] = useState<any[]>([]);
  const [actions, setActions] = useState<any[]>([]);
  const [selectedActionIds, setSelectedActionIds] = useState<number[]>([]);
  const [selectedMenuIds, setSelectedMenuIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);

    Promise.all([
      dataProvider.getList('permissionMenus', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'id', order: 'ASC' },
        filter: {},
      }),
      dataProvider.getList('permissionActions', {
        pagination: { page: 1, perPage: 2000 },
        sort: { field: 'id', order: 'ASC' },
        filter: {},
      }),
      fetchCurrentRoleIdFromServer(),
    ])
      .then(async ([menusRes, actionsRes, currentRoleId]) => {
        console.log('[RoleCreate] Loaded menus:', menusRes.data);
        console.log('[RoleCreate] Loaded actions:', actionsRes.data);

        let currentRoleName: string | null = null;
        if (currentRoleId) {
          try {
            const roleRes = await dataProvider.getOne('permissionRoles', { id: currentRoleId });
            currentRoleName = roleRes.data?.roleName || roleRes.data?.name || null;
          } catch (error) {
            console.warn('[RoleCreate] Failed to load current role:', error);
          }
        }

        const restricted = isQaManagerRoleName(currentRoleName);
        const allowedMenuIds = restricted ? getManagedMenuIdSet(menusRes.data) : new Set<number>();

        if (restricted) {
          setMenus(filterMenusByScope(menusRes.data, allowedMenuIds));
          setActions(filterActionsByScope(actionsRes.data, allowedMenuIds));
        } else {
          setMenus(menusRes.data);
          setActions(actionsRes.data);
        }

        // 默认全选所有 action
        const visibleActions = restricted
          ? filterActionsByScope(actionsRes.data, allowedMenuIds)
          : actionsRes.data;
        setSelectedActionIds(visibleActions.map((a: any) => a.id));

        // 找出没有 action 的菜单，默认全选
        const visibleMenus = restricted
          ? filterMenusByScope(menusRes.data, allowedMenuIds)
          : menusRes.data;
        const menusWithActions = new Set(visibleActions.map((a: any) => a.menuId).filter((id: any) => id != null));
        const menusWithoutActions = visibleMenus
          .filter((m: any) => !menusWithActions.has(m.id))
          .map((m: any) => m.id);
        console.log('[RoleCreate] Menus without actions (pre-selected):', menusWithoutActions);
        setSelectedMenuIds(menusWithoutActions);
      })
      .catch((err) => {
        console.error('[RoleCreate] Failed to load menus or actions:', err);
        notify('Failed to load menus or actions', { type: 'warning' });
      })
      .finally(() => setLoading(false));
  }, [dataProvider, notify]);

  if (loading) return <span>Loading...</span>;

  // 按 menuId 分组 actions
  const actionsByMenu = actions.reduce((acc: Record<string, any[]>, action: any) => {
    const key = action.menuId ?? 'none';
    acc[key] = acc[key] || [];
    acc[key].push(action);
    return acc;
  }, {});

  // 一级菜单：无 parentId；二级菜单：有 parentId，挂在对应一级下
  const sortByTitle = (a: any, b: any) =>
    String(a.title || '').localeCompare(String(b.title || ''));
  const level1Menus = menus.filter((m: any) => m.parentId == null).sort(sortByTitle);
  const childrenMap = new Map<string, any[]>();
  menus.forEach((m: any) => {
    if (m.parentId == null) return;
    const key = String(m.parentId);
    if (!childrenMap.has(key)) childrenMap.set(key, []);
    childrenMap.get(key)!.push(m);
  });
  childrenMap.forEach((arr) => arr.sort(sortByTitle));

  const toggleAction = (id: number) => {
    setSelectedActionIds(prev =>
      prev.includes(id)
        ? prev.filter(x => x !== id)
        : [...prev, id]
    );
  };

  const toggleMenuWithoutActions = (menuId: number) => {
    setSelectedMenuIds(prev =>
      prev.includes(menuId)
        ? prev.filter(x => x !== menuId)
        : [...prev, menuId]
    );
  };

  const toggleMenu = (menuActions: any[]) => {
    const ids = menuActions.map(a => a.id);
    const allSelected = ids.every(id => selectedActionIds.includes(id));

    setSelectedActionIds(prev =>
      allSelected
        ? prev.filter(id => !ids.includes(id))
        : Array.from(new Set([...prev, ...ids]))
    );
  };

  const allActionIds = actions
    .map((a: any) => Number(a.id))
    .filter(Number.isFinite);

  const menuIdsWithoutActions = menus
    .filter((m: any) => !(actionsByMenu[m.id] || []).length)
    .map((m: any) => m.id);

  const handleSelectAll = () => {
    setSelectedActionIds(allActionIds);
    setSelectedMenuIds(menuIdsWithoutActions);
  };

  const handleClearAll = () => {
    setSelectedActionIds([]);
    setSelectedMenuIds([]);
  };

  const selectedMenuCount = Array.from(
    new Set([
      ...actions
        .filter((a: any) => selectedActionIds.includes(Number(a.id)))
        .map((a: any) => a.menuId)
        .filter((id: any) => id != null),
      ...selectedMenuIds,
    ])
  ).length;

  const handleSave = async (data: any) => {
    try {
      // 合并所有 menuIds：1. 从选中的 actions 推导的 menuIds 2. 直接选中的没有 action 的菜单
      const menuIdsFromActions = Array.from(
        new Set(
          actions
            .filter((a: any) => selectedActionIds.includes(Number(a.id)))
            .map((a: any) => a.menuId)
            .filter((id: any) => id != null)
        )
      );
      const allMenuIds = Array.from(new Set([...menuIdsFromActions, ...selectedMenuIds]));
      
      console.log('[RoleCreate handleSave]');
      console.log('  selectedActionIds:', selectedActionIds);
      console.log('  selectedMenuIds (menus without actions):', selectedMenuIds);
      console.log('  menuIdsFromActions (derived from selected actions):', menuIdsFromActions);
      console.log('  Final allMenuIds to save:', allMenuIds);
      
      const payload = {
        roleName: data.roleName,
        description: data.description,
        menuIds: allMenuIds,
        actionIds: selectedActionIds,
      };

      console.log('[RoleCreate] Saving payload:', payload);
      
      await create('permissionRoles', { data: payload });
      notify('Role created successfully', { type: 'success' });
      
      // 等待一个短的延迟确保后端完全处理数据
      setTimeout(() => {
        redirect('list', 'permissionRoles');
      }, 500);
    } catch (error) {
      console.error('Create error:', error);
      notify('Create failed', { type: 'error' });
    }
  };

  return (
    <SimpleForm
      sx={{
        background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
        border: '1px solid #e5e7eb',
        borderRadius: '16px',
        p: 3,
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.04)',
        '& > .MuiStack-root': {
          flex: 1,
          overflow: 'visible',
          flexDirection: 'column',
          alignItems: 'stretch',
        },
      }}
      onSubmit={handleSave}
      toolbar={<RoleFormToolbar />}
    >
        <FormSyncComponent selectedActionIds={selectedActionIds} />
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: '14px',
            border: '1px solid #e2e8f0',
            bgcolor: '#ffffff',
            mb: 2,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography variant="subtitle1" fontWeight={700}>
              Basic Information
            </Typography>
            <Chip size="small" label="Create Role" sx={{ bgcolor: '#eef2ff', color: '#3730a3', fontWeight: 600 }} />
          </Stack>
          <Divider sx={{ mb: 2 }} />
          <Box display="flex" flexWrap="wrap" gap={2} sx={{ flexShrink: 0 }}>
          <Box flex="1 1 48%">
            <TextInput
              source="roleName"
              label="Role Name"
              fullWidth
              validate={[required()]}
            />
          </Box>

          <Box flex="1 1 48%">
            <TextInput
              source="description"
              label="Description"
              fullWidth
            />
          </Box>
          </Box>
        </Paper>

        <Box
          flex="1 1 100%"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'visible',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: '14px',
              border: '1px solid #e2e8f0',
              bgcolor: '#ffffff',
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5} flexWrap="wrap" gap={1}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="subtitle1" fontWeight={700}>
                  Menus & Actions
                </Typography>
                <Chip size="small" label={`Actions: ${selectedActionIds.length}`} color="primary" variant="outlined" />
                <Chip size="small" label={`Menus: ${selectedMenuCount}`} color="success" variant="outlined" />
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" size="small" onClick={handleSelectAll}>Select All</Button>
                <Button variant="text" size="small" color="inherit" onClick={handleClearAll}>Clear</Button>
              </Stack>
            </Stack>
            <Divider sx={{ mb: 1.5 }} />

            <Box
              sx={{
                flex: 1,
                overflow: 'visible',
                py: 0.5,
                pr: 0.5,
                '& .MuiFormControlLabel-root': { mr: 0 },
              }}
            >
              {level1Menus.map((menu1) => {
                const level2Menus = childrenMap.get(String(menu1.id)) || [];
                const actions1 = actionsByMenu[menu1.id] || [];
                
                // Build all ids for this menu and its actions
                const allIds1 = actions1.map((a: any) => a.id);
                const allIds2 = level2Menus.flatMap((m: any) =>
                  (actionsByMenu[m.id] || []).map((a: any) => a.id)
                );
                const allIds = [...allIds1, ...allIds2];
                
                // Check if menu1 has any actions (directly or through children)
                const hasAnyActions = allIds.length > 0;
                const allChecked1 = hasAnyActions
                  ? allIds.every((id) => selectedActionIds.includes(id))
                  : selectedMenuIds.includes(menu1.id);

                return (
                  <Paper key={menu1.id} elevation={0} sx={{ mb: 1.5, border: '1px solid #e5e7eb', borderRadius: '12px', p: 1.25, bgcolor: '#fcfdff' }}>
                    {/* 一级菜单 - show all menus regardless of actions */}
                    <Box sx={{ py: 0.25, display: 'flex', alignItems: 'center' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={allChecked1}
                            onChange={() => {
                              if (hasAnyActions) {
                                // 有 actions：切换 actions 选中状态
                                toggleMenu(
                                  allIds.length
                                    ? actions.filter((a: any) => allIds.includes(a.id))
                                    : []
                                );
                              } else {
                                // 没有 actions：直接切换菜单选中状态
                                toggleMenuWithoutActions(menu1.id);
                              }
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2" fontWeight="600">
                            {menu1.title}
                          </Typography>
                        }
                      />
                    </Box>

                    {/* 一级菜单下的 actions（若有） */}
                    {actions1.length > 0 && (
                      <Box sx={{ pl: 4, mb: 0.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {actions1.map((action: any) => (
                          <FormControlLabel
                            key={action.id}
                            control={
                              <Checkbox
                                size="small"
                                checked={selectedActionIds.includes(action.id)}
                                onChange={() => toggleAction(action.id)}
                              />
                            }
                            label={<Chip size="small" label={action.actionName} variant="outlined" />}
                          />
                        ))}
                      </Box>
                    )}

                    {/* 二级菜单及各自 actions - show all level2 menus even without actions */}
                    {level2Menus.map((menu2) => {
                      const menu2Actions = actionsByMenu[menu2.id] || [];
                      const hasActions2 = menu2Actions.length > 0;
                      const allChecked2 = hasActions2
                        ? menu2Actions.every((a: any) =>
                            selectedActionIds.includes(a.id)
                          )
                        : selectedMenuIds.includes(menu2.id);
                      
                      return (
                        <Box key={menu2.id} sx={{ pl: 4, mb: 0.75 }}>
                          <Box sx={{ py: 0.25 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={allChecked2}
                                  onChange={() => {
                                    if (hasActions2) {
                                      // 有 actions：切换 actions 选中状态
                                      toggleMenu(menu2Actions);
                                    } else {
                                      // 没有 actions：直接切换菜单选中状态
                                      toggleMenuWithoutActions(menu2.id);
                                    }
                                  }}
                                />
                              }
                              label={
                                <Typography variant="body2" fontWeight="500">
                                  {menu2.title}
                                </Typography>
                              }
                            />
                          </Box>
                          {menu2Actions.length > 0 && (
                            <Box sx={{ pl: 4, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                              {menu2Actions.map((action: any) => (
                                <FormControlLabel
                                  key={action.id}
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={selectedActionIds.includes(action.id)}
                                      onChange={() => toggleAction(action.id)}
                                    />
                                  }
                                  label={<Chip size="small" label={action.actionName} variant="outlined" />}
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Paper>
                );
              })}
            </Box>
          </Paper>
        </Box>
    </SimpleForm>
  );
};

const RoleCreate: React.FC = () => (
  <CreateBase resource="permissionRoles">
    <RoleCreateForm />
  </CreateBase>
);

export default RoleCreate;
